import { pubsub } from '@services';
import { notyf } from '@modules';
import { debugError, debugLog } from '@utils';

class PicturatorUploadWidget extends HTMLElement {
  static debug = false;

  constructor() {
    super();
    this.id = this.dataset.id;
    this.picturatorUrl = this.dataset.picturatorUrl || '/';
    this.galleryUrl = this.dataset.galleryUrl || '/pages/gallery';
    this.redirectUrl = this.dataset.redirectUrl || '';
    this.preselectData = JSON.parse(this.dataset.preselect || '{}');
    if (!this.id) {
      throw new Error('A unique ID is required for upload button');
    }

    // Config
    this.config = {
      containerId: `${this.id}`,
      shop: window.theme.shop || 'STIT',
      env: window.theme.env || 'production',
      campaign: window.theme.campaign.handle,
      channel: window.theme.channel.handle || 'seo',
      tier: parseInt(window.theme.tier || 0),
      priceTier: parseInt(window.theme.tier || 0),
      buttonText: 'Upload',
      maxFileCount: 25,
      allowMultiple: true,
    };
    try {
      this.config = { ...this.config, ...JSON.parse(this.dataset.config || '{}') };
    } catch (error) {
      debugError('Failed to parse configurator liquid config:', error);
      this.config = { ...this.config };
    }
  }

  connectedCallback() {
    const config = {
      ...this.config,
    };

    // Initialize the configurator
    debugLog('Picturator: Initialize upload widget', config);
    this.uploadWidget = window.UploadWidget.init(config);

    // Event listeners
    this.uploadWidget.on('upload_ready', this.#onUploadReady.bind(this));
    this.uploadWidget.on('upload_success', this.#onUploadSuccess.bind(this));
    this.uploadWidget.on('upload_error', this.#onUploadError.bind(this));
  }

  disconnectedCallback() {
    if (!this.uploadWidget) return;
    this.uploadWidget.off('upload_ready', this.#onUploadReady.bind(this));
    this.uploadWidget.off('upload_success', this.#onUploadSuccess.bind(this));
    this.uploadWidget.off('upload_error', this.#onUploadError.bind(this));

    // Destroy the upload widget
    this.uploadWidget.destroy();

    // Reset the upload widget reference
    this.uploadWidget = null;
  }

  /**
   * Handle upload ready event
   */
  #onUploadReady() {
    PicturatorUploadWidget.debug && debugLog('Picturator: Upload widget is ready!');
  }

  /**
   * Handle upload success event
   * @param {*} result
   */
  #onUploadSuccess(result) {
    PicturatorUploadWidget.debug && debugLog('Picturator: Upload widget is successful!', result);
    const keys = Array.isArray(result?.files)
      ? result.files.map((f) => f?.uploadKey).filter(Boolean)
      : [];
    const uploadKey = keys[0];
    if (!uploadKey) {
      debugError('Picturator: Upload key not found');
      return;
    }
    this.handleUpload(keys);
  }

  /**
   * Handle upload error event
   * @param {*} error
   */
  #onUploadError(error) {
    PicturatorUploadWidget.debug && debugLog('Picturator: Upload widget is error!', error);

    // Normalize error message
    const rawMessage = typeof error === 'string' ? error : error?.message || '';
    const lowerMessage = rawMessage.toLowerCase();

    // Try to map to localized toast translations
    let localizedMessage = rawMessage;

    // DPI / resolution-specific error
    if (lowerMessage.includes('dpi') || lowerMessage.includes('resolution')) {
      localizedMessage =
        window.toast_translations?.upload?.low_dpi ||
        window.toast_translations?.upload?.generic_error ||
        rawMessage;
    } else {
      localizedMessage = window.toast_translations?.upload?.generic_error || rawMessage;
    }

    // Show localized error notification
    if (localizedMessage) {
      notyf.error(localizedMessage);
    }
  }

  /**
   * Handle the upload of the image
   * @param {*} uploadKey
   */
  handleUpload(uploadKeys) {
    const keys = Array.isArray(uploadKeys)
      ? uploadKeys.filter(Boolean)
      : [uploadKeys].filter(Boolean);
    const firstKey = keys[0];
    if (!firstKey) {
      debugError('Picturator: Upload key not found');
      return;
    }

    const preselectParams = Object.entries(this.preselectData)
      .map(([key, val]) => `${key}=${val}`)
      .join('&');

    // If redirectUrl is provided (e.g. Photo Prints), always go there (not gallery/configurator).
    if (this.redirectUrl) {
      const joiner = this.redirectUrl.includes('?') ? '&' : '?';
      const keyParams = keys.length > 1 ? `uploadKeys=${keys.join(',')}` : `uploadKey=${firstKey}`;
      const url = `${this.redirectUrl}${joiner}${keyParams}${preselectParams ? `&${preselectParams}` : ''}`;
      window.location.href = url;
      return;
    }

    // Multiple images uploaded → redirect to gallery page so user can select which to configure
    if (keys.length > 1 && this.galleryUrl) {
      // Store preselect context in sessionStorage for the gallery page to use
      try {
        sessionStorage.setItem(
          'picturator_gallery_context',
          JSON.stringify({
            preselect: this.preselectData,
            picturatorUrl: this.picturatorUrl,
          })
        );
      } catch (e) {}
      // Pass uploadKeys through the URL so the gallery has an authoritative
      // list for this upload session, independent of IndexedDB availability.
      const joiner = this.galleryUrl.includes('?') ? '&' : '?';
      window.location.href = `${this.galleryUrl}${joiner}uploadKeys=${keys.join(',')}`;
      return;
    }

    // Single image uploaded → redirect to configurator
    if (window.location.pathname !== this.picturatorUrl) {
      const preselectParams = Object.entries(this.preselectData)
        .map(([key, val]) => `${key}=${val}`)
        .join('&');
      const url = `${this.picturatorUrl}?uploadKey=${firstKey}&${preselectParams}`;
      window.location.href = url;
    } else {
      pubsub.publish(`upload:image`, {
        key: keys.length ? keys : [firstKey],
        preselect: this.preselectData,
      });
    }
  }
}

customElements.define('picturator-upload-widget', PicturatorUploadWidget);
